// Standardised section header for the Builder tab (and anywhere else that wants the
// same shape). Title + optional one-line subtitle + optional right-side badge + optional
// rich help (revealed via a HelpCircle hover-card).

import type { ReactNode } from "react";
import { HelpCircle } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Badge } from "@/components/ui/badge";

interface Props {
  title: string;
  subtitle?: string;
  badge?: ReactNode;
  help?: string;
}

export function SectionHeader({ title, subtitle, badge, help }: Props) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold tracking-tight flex items-center gap-1.5">
          {title}
          {help && (
            <HoverCard openDelay={150}>
              <HoverCardTrigger asChild>
                <button type="button" className="text-muted-foreground hover:text-foreground transition-colors">
                  <HelpCircle className="size-3.5" />
                </button>
              </HoverCardTrigger>
              <HoverCardContent className="text-xs max-w-[320px]">
                {help}
              </HoverCardContent>
            </HoverCard>
          )}
        </h3>
        {badge != null && (
          typeof badge === "string" || typeof badge === "number"
            ? <Badge variant="secondary" className="text-[10px] font-mono">{badge}</Badge>
            : <div className="text-[11px] text-muted-foreground">{badge}</div>
        )}
      </div>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-1 leading-snug">
          {subtitle}
        </p>
      )}
    </div>
  );
}
